import { InjectionKey, Ref, inject, ref, watch } from 'vue'

const themes = [
	'default',
	'customer',
	'driver',
	'curator',
	'shipper',
	'carrier',
	'admin'
] as const
export type Theme = (typeof themes)[number]

const THEME_KEY = 'theme.name'
const IS_DARK_KEY = 'theme.isDark'

export interface ThemeInstance {
	themes: string[]

	theme: Ref<Theme>
	isDark: Ref<boolean>

	setTheme: (_value: Theme) => void
	toggleDark: () => void
}

export interface ThemeOptions {
	default?: {
		name: Theme
		dark?: boolean
	}
	themes?: Theme[]
	ignoreThemes?: Theme[]
}

export const ThemeSymbol: InjectionKey<ThemeInstance> =
	Symbol.for('ui-kit:theme')

export function initiateTheme(
	options: ThemeOptions = { default: { name: 'default', dark: false } }
) {
	const theme = ref<Theme>(
		(localStorage.getItem(THEME_KEY) as Theme) || options.default?.name
	)
	const isDark = ref<boolean>(
		localStorage.getItem(IS_DARK_KEY) === 'true' ||
			options.default?.dark ||
			false
	)

	let _themes = themes.filter((t) => !options.ignoreThemes?.includes(t))
	if (options.themes) {
		_themes = _themes.filter((t) => options.themes?.includes(t))
	}

	const setTheme = (value: Theme) => {
		theme.value = value
	}

	const toggleDark = () => {
		isDark.value = !isDark.value
	}

	watch(
		theme,
		(value) => {
			localStorage.setItem(THEME_KEY, value)
			document.documentElement.setAttribute('data-theme', value)
		},
		{ immediate: true }
	)

	watch(
		isDark,
		(value) => {
			localStorage.setItem(IS_DARK_KEY, value ? 'true' : 'false')
			if (value) {
				document.documentElement.setAttribute('data-theme-mode', 'dark')
			} else {
				document.documentElement.setAttribute('data-theme-mode', 'light')
			}
		},
		{ immediate: true }
	)

	return {
		themes: _themes,
		theme,
		isDark,
		setTheme,
		toggleDark
	} as ThemeInstance
}

export function useTheme() {
	const theme = inject(ThemeSymbol)

	if (!theme) {
		throw new Error('[UiKit] Could not find theme instance')
	}

	return theme
}
